import React from 'react';
import { ArrowLeft, Gift, DollarSign, Users, CheckCircle, XCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';

const ReferralTerms = () => {
  const navigate = useNavigate();
  const lastUpdated = 'December 15, 2025';

  return (
    <div className="min-h-screen bg-[#0a0b0c]">
      {/* Header */}
      <div className="bg-[#131416] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-white/70 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          <img 
            src="https://customer-assets.emergentagent.com/job_tactical-sports/artifacts/00wc7m32_GWINS%20Gear%20Logo.jpg" 
            alt="GWINS" 
            className="h-8 w-auto"
          />
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <Gift className="h-16 w-16 text-[#00d4aa] mx-auto mb-4" />
          <h1 className="text-4xl font-black text-white uppercase mb-2">Referral Program Terms</h1>
          <p className="text-white/60">Last updated: {lastUpdated}</p>
        </div>

        <div className="space-y-8 text-white/80">
          {/* Overview */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-[#00d4aa]" />
              Program Overview
            </h2>
            <p className="leading-relaxed">
              The GWINS Gear Referral Program rewards our community members for spreading the word about our products. When you refer friends and they make a purchase, both you and your friend can earn rewards!
            </p>
          </section>

          {/* How It Works */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">How It Works</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] font-bold flex-shrink-0">1</div>
                <div>
                  <h3 className="text-white font-semibold">Get Your Referral Code</h3>
                  <p className="text-white/70">Sign up and receive your unique referral code from your account dashboard.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] font-bold flex-shrink-0">2</div>
                <div>
                  <h3 className="text-white font-semibold">Share With Friends</h3>
                  <p className="text-white/70">Share your code with friends, teammates, or on social media.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] font-bold flex-shrink-0">3</div>
                <div>
                  <h3 className="text-white font-semibold">They Get a Discount</h3>
                  <p className="text-white/70">Your friend uses your code at checkout and receives a discount on their first order.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] font-bold flex-shrink-0">4</div>
                <div>
                  <h3 className="text-white font-semibold">You Earn Rewards</h3>
                  <p className="text-white/70">Once their order is confirmed, you receive store credit or cash rewards!</p>
                </div>
              </div>
            </div>
          </section>

          {/* Rewards */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-[#00d4aa]" />
              Rewards Structure
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-[#00d4aa]/10 rounded-lg p-4 border border-[#00d4aa]/20">
                <h3 className="text-[#00d4aa] font-bold mb-2">For You (Referrer)</h3>
                <p className="text-white/70">Earn 15% store credit on each successful referral purchase</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <h3 className="text-white font-bold mb-2">For Your Friend</h3>
                <p className="text-white/70">10% off their first order when using your referral code</p>
              </div>
            </div>
          </section>

          {/* Eligibility */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-[#00d4aa]" />
              Eligibility Requirements
            </h2>
            <ul className="list-disc list-inside space-y-2 text-white/70">
              <li>You must have a registered GWINS Gear account</li>
              <li>Referred friends must be new customers (no previous orders)</li>
              <li>The referral code must be applied at checkout before purchase</li>
              <li>Minimum order value of $50 required for referral rewards</li>
              <li>Rewards are issued after the return/refund period has passed (30 days)</li>
            </ul>
          </section>

          {/* Restrictions */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <XCircle className="h-5 w-5 text-red-400" />
              Restrictions & Prohibited Activities
            </h2>
            <ul className="list-disc list-inside space-y-2 text-white/70">
              <li>Self-referrals are not permitted</li>
              <li>Creating fake accounts to generate referrals is prohibited</li>
              <li>Referral codes cannot be posted on coupon/deal websites</li>
              <li>Paid advertising of referral codes is not allowed</li>
              <li>Rewards cannot be combined with other promotions unless specified</li>
              <li>We reserve the right to void rewards obtained through fraudulent means</li>
            </ul>
          </section>

          {/* Program Changes */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Program Changes & Termination</h2>
            <p className="text-white/70">
              GWINS Gear reserves the right to modify, suspend, or terminate the referral program at any time without prior notice. Any earned rewards will be honored, but pending referrals may be affected by program changes. We may also terminate individual participation for violation of these terms.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-[#131416] rounded-xl p-6 border border-white/10">
            <h2 className="text-xl font-bold text-white mb-4">Questions?</h2>
            <p>
              If you have any questions about the Referral Program, please contact us at:
            </p>
            <p className="mt-4 text-[#00d4aa] font-semibold">info@gwinsgear.com</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ReferralTerms;
